<template>
  <div
    :class="[
      brandClasses?.base,
      brandClasses?.variant?.[variant]?.[disabled ? 'disabled' : (invalid && !focused ? 'invalid' : 'default')],
      { [brandClasses?.variant?.[variant]?.focused || brandClasses?.focused!]: focused },
    ]"
    data-test-id="vf-input"
  >
    <!-- @slot Slot is meant for phone code select to the left of input. But may be used for any content -->
    <slot name="before" />

    <label v-bind="attrs" class="relative f-col grow justify-center px-3" style="cursor: text">
      <slot name="label" v-bind="{ focused }">
        <span v-if="showInputOffset" class="pointer-events-none block h-4" />
        <!--    Input offset    -->
        <span
          class="pointer-events-none absolute-0 flex items-center px-3 duration"
          :class="{
            [brandClasses.labelWrapper]: isLabelOffset,
            'c-grey-10': focused && !invalid && !variant?.startsWith('inverse'),
          }"
          data-test-id="vf-input-label"
        >
          <span class="min-w-0 flex gap-1" :class="isLabelOffset ? brandClasses?.variant?.[variant]?.label : ''">
            <span v-if="label" v-bind="propsLabel" class="truncate">{{ label }}</span>
            <span v-if="label && required" aria-hidden="true">*</span>
          </span>
        </span>
      </slot>

      <!-- @slot Slot is meant for passing `input` element for lower level configurations -->
      <span :class="{ 'self-start': type === 'date' }" @focusin="focused = true" @focusout="focused = false">
        <slot>
          <base-input
            ref="inputRef"
            v-bind="{
              ...props,
              'aria-describedby': componentAttrs['aria-describedby'],
              'aria-invalid': invalid,
              ...propsInput,
            }"
            v-model="modelValue"
            :inputmode
            :type
            @blur="$emit('blur', $event)"
            @change="$emit('change', $event)"
            @focus="$emit('focus', $event)"
            @input="$emit('input', $event)"
          />
        </slot>
      </span>
    </label>
    <div class="pointer-within flex items-center self-center gap-1 pr-3">
      <!-- @slot Slot is meant for `icon` to the right of input. But may be used for any content -->
      <slot name="after" />
      <vf-icon
        v-show="isIconShown"
        class="pointer-events-none"
        :class="!invalid ? 'c-green-30' : variant === 'base' ? 'c-red-30' : 'c-red-60'"
        :name="invalid ? 'error' : 'success'"
        size="md"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
export type Props = {
  /**
   * Defines disabled state
   */
  disabled?: boolean
  /**
   * Defines inputmode attribute
   */
  inputmode?: 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search'
  /**
   * Defines validation state
   * There are 3 possible states
   * Indeterminate === undefined (no validation icons visible)
   * Valid === true
   * Invalid === false
   */
  invalid?: boolean
  /**
   * Hides validation icons for this input instance
   */
  hideValidationIcons?: boolean
  /**
   * Label is always required for accessibility purposes
   * if you want to hide it, use hideLabel prop
   */
  label?: string
  /**
   * Defines the maximum value that is acceptable
   * Works with types: number, date
   */
  max?: string
  /**
   * Defines the minimum value that is acceptable
   * Works with types: number, date
   */
  min?: string
  /**
   * Defines input name
   */
  name?: string
  /**
   * Allows passing additional attributes to the label element
   * E.g. `id` and `class` for accessibility purposes
   */
  propsLabel?: Record<string, unknown>
  /**
   * Allows passing additional attributes to the input element
   * E.g. `id` and `class` for accessibility purposes
   */
  propsInput?: Record<string, unknown>
  /**
   * Defines if the input is required for accessibility purposes
   */
  required?: boolean
  /**
   * Defines type of the input
   * do not specify 'text' type as it is a default value
   * 'text' is left as an allowed value for cases of conditional logic
   */
  type?: 'text' | 'email' | 'number' | 'search' | 'tel' | 'password' | 'date'
  /**
   * Defines variant for inverse/dark backgrounds
   * `inverse-transparent` renders a see-through field (no solid fill) for brands/regions
   * whose `inverse` variant is otherwise a solid color.
   */
  variant?: 'inverse' | 'inverse-transparent' | 'base'
}

export type Emits = {
  blur: [value: string]
  change: [value: string]
  focus: [value: string]
  input: [value: string]
}

export type Slots = {
  after?: () => void
  before?: () => void
  default?: () => void
  label?: (props: { focused: boolean }) => void
}

const {
  hideValidationIcons,
  /**
   * Needs to be explicitly set to `undefined` for indeterminate validation state
   */
  invalid = undefined,
  label,
  propsInput,
  propsLabel,
  type,
  variant = 'base',
  ...props
} = defineProps<Props>()

defineEmits<Emits>()

defineSlots<Slots>()

const inputRef = useTemplateRef('inputRef')
const modelValue = defineModel<string | number>()
const focused = ref(false)

const { showErrorIcon, showSuccessIcon, showInputOffset, brandClasses } = useAppConfig().components.vf.input
const componentAttrs = useAttrs()
const { class: _, 'aria-describedby': __, ...attrs } = componentAttrs

const isIconShown = computed(() => {
  if (hideValidationIcons) return false
  if (showErrorIcon && showSuccessIcon) return invalid !== undefined
  if (showErrorIcon) return invalid === true
  if (showSuccessIcon) return invalid === false
})
const isLabelOffset = computed(() => modelValue.value || focused.value || type === 'date')

defineExpose({
  inputRef
})
</script>
